"use client";

import { AuthContainer, AuthProvider } from "@pharaday/auth";
import { Layout, LayoutGridIcon, LogOutIcon, UserIcon } from "@pharaday/lighthouse";
import { NamespaceContainerProvider, useNamespaceId } from "@pharaday/namespace";
import { intersection } from "@pharaday/utils";
import { useRouter } from "next/navigation";
import { useMemo } from "react";

import {
  ORGANIZATION_PROFILE_QUERY_KEY,
  useQueryAccessibleTools,
  useQueryNamespaceIdentification,
  useQueryOrganizationProfile,
  useQueryUserNamespaces,
  useQueryUserProfile,
  USER_PROFILE_QUERY_KEY,
} from "../../apis";
import { AuthenticatedContainer } from "../../core/authenticated.container";
import type { Namespace } from "../../core/domain/entities/namespace";
import { ToolTypeEnum } from "../../core/domain/enums/tool-type.enum";
import NavigationCommandProvider from "../navbar-header/NavigationCommandProvider";
import { resetCrispSession } from "../support-chat";
import useToolEnabled from "../tool-guard/useToolEnabled";

const UserMenu = ({ compactMode }: { compactMode?: boolean }) => {
  const { namespaceId, setNamespaceId, resetNamespaceId } = useNamespaceId();
  const router = useRouter();

  const { data: userProfile } = useQueryUserProfile();
  const { data: companyProfile } = useQueryOrganizationProfile();
  const { data: userNamespaces } = useQueryUserNamespaces();
  const { data: namespaceIdentification } = useQueryNamespaceIdentification(namespaceId);
  const { data: tools } = useQueryAccessibleTools();

  const businessSetting = useMemo(() => {
    return tools?.find(tool => tool.type === ToolTypeEnum.BUSINESS_SETTINGS);
  }, [tools]);

  const isAdminEnabled = useToolEnabled(ToolTypeEnum.ADMIN);

  const menuItems = useMemo(() => {
    return [
      ...(namespaceId
        ? [
            {
              Icon: UserIcon,
              label: "User Profile",
              url: `/user-profile`,
            },
            {
              separator: true,
            },
          ]
        : []),
      ...(isAdminEnabled
        ? [
            {
              Icon: LayoutGridIcon,
              label: "Admin Console",
              url: `/admin`,
            },
            {
              separator: true,
            },
          ]
        : []),

      {
        color: "error" as const,
        Icon: LogOutIcon,
        label: "Log out",
        handler: () => {
          resetCrispSession();
          resetNamespaceId();
          void AuthContainer.get(AuthProvider).signOut();
        },
      },
    ];
  }, [isAdminEnabled, resetNamespaceId]);

  const namespace = useMemo(() => {
    const result = userNamespaces?.find(ns => ns.id === namespaceId);
    return result
      ? {
          ...result,
          spaceSettings: businessSetting ? { urlRoot: businessSetting.urlRoot } : undefined,
        }
      : undefined;
  }, [userNamespaces, namespaceId, businessSetting]);

  const handleNamespaceChange = async (namespace: Namespace) => {
    setNamespaceId(namespace.id);
    router.refresh(); // refresh to trigger AppRouteGuard validation
  };

  return (
    <Layout.UserMenu
      userInfo={userProfile}
      companyProfile={companyProfile}
      items={menuItems}
      namespace={
        namespace && namespaceIdentification
          ? {
              ...namespace,
              avatarUrl: namespaceIdentification.logoUrl,
            }
          : namespace
      }
      userNamespaces={userNamespaces}
      compactMode={compactMode}
      onNamespaceChange={handleNamespaceChange}
    />
  );
};

export const UserMenuHOC = ({ compactMode }: { compactMode?: boolean }) => {
  return (
    <NamespaceContainerProvider
      container={AuthenticatedContainer}
      resetQueriesPredicate={query =>
        intersection(query.queryKey, [ORGANIZATION_PROFILE_QUERY_KEY, USER_PROFILE_QUERY_KEY]).length === 0
      }
    >
      <NavigationCommandProvider>
        <UserMenu compactMode={compactMode} />
      </NavigationCommandProvider>
    </NamespaceContainerProvider>
  );
};

export default UserMenuHOC;
